import React, { useState } from "react";
import { UserWithRole } from "@/types/iam";
import { setAdminClaim, setStaffClaim, setDeveloperClaim, setObserverClaim } from "@/services/firebase/functions";
import { useAuth } from "@/hooks/useAuth";
import RoleEditDialog from "./RoleEditDialog";

interface UserRoleManagerProps {
  user: UserWithRole;
  onUpdate: () => void;
}

export default function UserRoleManager({ user, onUpdate }: UserRoleManagerProps) {
  const { user: currentUser, refreshUserPermissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState(false);

  const handleOpenDialog = () => {
    if (!currentUser?.isAdmin) {
      setError("只有管理員可以修改權限");
      return;
    }
    setError(null);
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setError(null);
  };

  const handleSave = async (isAdmin: boolean, isStaff: boolean, isDeveloper: boolean, isObserver: boolean) => {
    if (!currentUser?.isAdmin) {
      setError("只有管理員可以修改權限");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 檢查是否需要更新 Admin 權限
      if (user.isAdmin !== isAdmin) {
        await setAdminClaim(user.uid, isAdmin);
      }

      // 檢查是否需要更新 Staff 權限
      if (user.isStaff !== isStaff) {
        await setStaffClaim(user.uid, isStaff);
      }

      // 檢查是否需要更新 Developer 權限
      if (user.isDeveloper !== isDeveloper) {
        await setDeveloperClaim(user.uid, isDeveloper);
      }

      // 檢查是否需要更新 Observer 權限
      if (user.isObserver !== isObserver) {
        await setObserverClaim(user.uid, isObserver);
      }

      // 如果修改的是當前用戶，刷新權限（強制刷新 token 以獲取最新的 Custom Claims）
      if (currentUser?.uid === user.uid) {
        // 等待一下讓 Functions 完成 Custom Claims 和 Firestore 的同步
        await new Promise(resolve => setTimeout(resolve, 1500));
        await refreshUserPermissions();
      }

      // 等待一下讓 Functions 完成 Firestore 更新和審計日誌記錄
      setTimeout(() => {
        onUpdate();
        setShowDialog(false);
      }, 1000);
    } catch (err: any) {
      setError(err.message || "操作失敗");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleOpenDialog}
        disabled={loading || !currentUser?.isAdmin}
        className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
      >
        權限調整
      </button>

      {error && (
        <div className="mt-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <RoleEditDialog
        isOpen={showDialog}
        user={user}
        onClose={handleCloseDialog}
        onSave={handleSave}
        loading={loading}
      />
    </>
  );
}

